import { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Edit2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailTemplates = () => {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editData, setEditData] = useState({ subject: '', html_content: '' });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setEditData({
      subject: template.subject,
      html_content: template.html_content
    });
    setShowEditModal(true);
  };

  const handleSave = async () => {
    try {
      await axios.put(
        `${API}/admin/email-templates/${selectedTemplate.id}`,
        editData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template updated successfully!');
      setShowEditModal(false);
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to update template');
    }
  };

  const handlePreview = (template) => {
    setSelectedTemplate(template);
    setShowPreviewModal(true);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Email Templates</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="border rounded-lg p-6"
                  data-testid={`template-${template.template_key}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <h3 className="font-bold">{template.template_name}</h3>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p><strong>Key:</strong> <code className="bg-gray-100 px-2 py-0.5 rounded">{template.template_key}</code></p>
                        <p><strong>Subject:</strong> {template.subject}</p>
                        {template.variables && template.variables.length > 0 && (
                          <p>
                            <strong>Variables:</strong> 
                            <span className="ml-2">
                              {template.variables.map((v, i) => (
                                <code key={i} className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded mr-1 text-xs">
                                  {v}
                                </code>
                              ))}
                            </span>
                          </p>
                        )}
                        <p className="text-xs text-gray-500">Last updated: {new Date(template.updated_at).toLocaleString()}</p>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePreview(template)}
                        data-testid={`preview-${template.template_key}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Preview
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleEdit(template)}
                        data-testid={`edit-${template.template_key}`}
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Edit Template: {selectedTemplate?.template_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Subject Line</Label>
              <Input
                value={editData.subject}
                onChange={(e) => setEditData({ ...editData, subject: e.target.value })}
                className="mt-1"
                placeholder="Email subject..."
              />
            </div>

            <div>
              <Label>HTML Content</Label>
              <Textarea
                value={editData.html_content}
                onChange={(e) => setEditData({ ...editData, html_content: e.target.value })}
                className="mt-1 font-mono text-sm"
                rows={20}
                placeholder="HTML template..."
              />
            </div>

            {selectedTemplate?.variables && (
              <div className="bg-blue-50 border border-blue-200 p-4 rounded">
                <p className="text-sm font-medium mb-2">Available Variables:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedTemplate.variables.map((v, i) => (
                    <code key={i} className="bg-white px-2 py-1 rounded text-xs">
                      {`{{${v}}}`}
                    </code>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button onClick={handleSave} className="flex-1">
                Save Changes
              </Button>
              <Button variant="outline" onClick={() => setShowEditModal(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Preview: {selectedTemplate?.template_name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="mb-4 p-4 bg-gray-50 rounded">
              <p className="text-sm text-gray-600">Subject:</p>
              <p className="font-medium">{selectedTemplate?.subject}</p>
            </div>
            <div 
              className="border p-4 bg-white" 
              dangerouslySetInnerHTML={{ __html: selectedTemplate?.html_content }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplates;
